import {
  getServicesPricingOfferingIconPresentation,
  resolveServicesPricingOfferingIconKey,
  resolveServicesPricingSkillIconKey,
  type ServicesPricingOfferingIconKey,
} from "./profileServicesPricingOfferingIcons";

function skillVisualToneFromIconKey(
  iconKey: ServicesPricingOfferingIconKey,
): ServicesPricingSkillVisualTone {
  switch (iconKey) {
    case "thermometer":
      return "thermal";
    case "camera":
    case "video":
      return "video";
    case "eye":
      return "inspection";
    case "box":
      return "modeling";
    case "map":
    default:
      return "map";
  }
}

export type ServicesPricingOfferingVisualTone =
  | "mapping"
  | "thermal"
  | "modeling"
  | "documentation"
  | "marketing"
  | "agriculture"
  | "default";

export type ServicesPricingSkillVisualTone =
  | "map"
  | "thermal"
  | "video"
  | "inspection"
  | "modeling"
  | "default";

export type ServicesPricingOfferingDisplay = Readonly<{
  id: string;
  title: string;
  description?: string;
  tagLabel?: string;
  deliverables: readonly string[];
  industries: readonly string[];
  priceLabel: string;
  priceMinCents?: number;
  turnaroundLabel?: string;
  iconKey: ServicesPricingOfferingIconKey;
  visualTone: ServicesPricingOfferingVisualTone;
  purchaseCount?: number;
  averageRating?: number;
  reviewCount?: number;
  featuredReviewerInitials?: readonly string[];
}>;

export type ServicesPricingConditionDisplay = Readonly<{
  id: string;
  title: string;
  description: string;
}>;

export type ServicesPricingSkillDisplay = Readonly<{
  id: string;
  name: string;
  validationCount: number;
  validationPercent: number;
  validatorInitials: readonly string[];
  iconKey: ServicesPricingOfferingIconKey;
  visualTone: ServicesPricingSkillVisualTone;
}>;

export type ServicesPricingTeamMemberDisplay = Readonly<{
  id: string;
  displayName: string;
  initials: string;
  roleTitle?: string;
}>;

export type ServicesPricingTeamSummaryDisplay = Readonly<{
  memberCount: number;
  languages: readonly string[];
  languageCount: number;
  yearsExperienceLabel: string;
  industriesServedLabel: string;
  industriesCount: number;
  missionsCompletedLabel: string;
  clientSatisfactionLabel: string;
  clientSatisfactionDetail?: string;
  members: readonly ServicesPricingTeamMemberDisplay[];
  extraMemberCount: number;
}>;

export type ServiceOfferingInput = Readonly<{
  id: string;
  serviceKey: string;
  serviceTitle: string;
  serviceCategory: string;
  description?: string;
  pricingModel: string;
  priceMinCents?: number;
  priceMaxCents?: number;
  currency?: string;
  turnaroundDaysMin?: number;
  turnaroundDaysMax?: number;
  serviceStatus: string;
  deliverables: readonly string[];
  serviceTagLabels?: readonly string[];
  targetIndustryLabels?: readonly string[];
  turnaroundLabel?: string;
  iconKey?: string;
  purchaseCount?: number;
  averageRating?: number;
  reviewCount?: number;
  featuredReviewerInitials?: readonly string[];
}>;

export type ServiceConditionInput = Readonly<{
  id: string;
  conditionKey: string;
  conditionTitle: string;
  conditionDescription: string;
  conditionStatus: string;
}>;

export type SkillInput = Readonly<{
  id: string;
  skillKey: string;
  skillLevel?: string;
  validationStatus: string;
  confirmationCount?: number;
  iconKey?: string;
  featuredValidatorInitials?: readonly string[];
}>;

export type TeamMemberInput = Readonly<{
  id: string;
  memberDisplayName: string;
  roleTitle?: string;
  memberStatus: string;
  visibilityConsentStatus: string;
  publicLanguages?: readonly string[];
  yearsExperience?: number;
  missionsCompleted?: number;
  clientSatisfactionPercent?: number;
  clientReviewCount?: number;
  publicIndustryLabels?: readonly string[];
}>;

export type TeamProfileInput = Readonly<{
  teamName?: string;
  teamSummary?: string;
  languages: readonly string[];
  capacityLabel?: string;
  teamStatus: string;
} | null>;

export const DEFAULT_OFFERING_INDUSTRIES = ["Real Estate", "Construction", "Energy", "Agriculture", "Media", "Infrastructure"] as const;

function initialsForName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0] ?? ""}${parts[parts.length - 1]![0] ?? ""}`.toUpperCase();
}

function formatCurrencyFromCents(cents: number, currency = "USD"): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

export function formatOfferingPriceLabel(input: Readonly<{
  pricingModel: string;
  priceMinCents?: number;
  priceMaxCents?: number;
  currency?: string;
  fromLabel?: string;
  quoteRequiredLabel?: string;
}>): string {
  if (input.pricingModel === "quote_required") {
    return input.quoteRequiredLabel ?? "Quote required";
  }
  const currency = input.currency ?? "USD";
  if (input.priceMinCents !== undefined && input.priceMaxCents !== undefined) {
    if (input.priceMinCents === input.priceMaxCents) {
      return formatCurrencyFromCents(input.priceMinCents, currency);
    }
    return `${formatCurrencyFromCents(input.priceMinCents, currency)} - ${formatCurrencyFromCents(input.priceMaxCents, currency)}`;
  }
  if (input.priceMinCents !== undefined) {
    return `${input.fromLabel ?? "From"} ${formatCurrencyFromCents(input.priceMinCents, currency)}`;
  }
  return input.quoteRequiredLabel ?? "Quote required";
}

export function formatOfferingTurnaroundLabel(
  turnaroundDaysMin?: number,
  turnaroundDaysMax?: number,
): string | undefined {
  if (turnaroundDaysMin === undefined && turnaroundDaysMax === undefined) return undefined;
  if (turnaroundDaysMin !== undefined && turnaroundDaysMax !== undefined) {
    return turnaroundDaysMin === turnaroundDaysMax
      ? `${turnaroundDaysMin} days`
      : `${turnaroundDaysMin}-${turnaroundDaysMax} days`;
  }
  if (turnaroundDaysMin !== undefined) return `${turnaroundDaysMin}+ days`;
  return `Up to ${turnaroundDaysMax} days`;
}

export function resolveOfferingVisualTone(input: Readonly<{
  serviceCategory: string;
  serviceTitle: string;
}>): ServicesPricingOfferingVisualTone {
  const haystack = `${input.serviceCategory} ${input.serviceTitle}`.toLowerCase();
  if (haystack.includes("lidar") || haystack.includes("3d") || haystack.includes("photogrammetry")) return "modeling";
  if (haystack.includes("thermal")) return "thermal";
  if (haystack.includes("agriculture") || haystack.includes("ndvi") || haystack.includes("crop")) return "agriculture";
  if (haystack.includes("real estate") || haystack.includes("photo") || haystack.includes("video") || haystack.includes("marketing")) return "marketing";
  if (haystack.includes("construction") || haystack.includes("documentation") || haystack.includes("progress")) return "documentation";
  if (haystack.includes("mapping") || haystack.includes("survey")) return "mapping";
  return "default";
}

export function resolveSkillVisualTone(skillKey: string, iconKey?: string): ServicesPricingSkillVisualTone {
  return skillVisualToneFromIconKey(resolveServicesPricingSkillIconKey(skillKey, iconKey));
}

export function buildServicesPricingOfferingDisplays(
  offerings: readonly ServiceOfferingInput[],
  labels?: Readonly<{ fromLabel?: string; quoteRequiredLabel?: string }>,
): readonly ServicesPricingOfferingDisplay[] {
  return offerings
    .filter((offering) => offering.serviceStatus === "listed" || offering.serviceStatus === "paused")
    .map((offering) => {
      const iconKey = resolveServicesPricingOfferingIconKey({
        iconKey: offering.iconKey,
        serviceCategory: offering.serviceCategory,
        serviceTitle: offering.serviceTitle,
        serviceTagLabels: offering.serviceTagLabels,
      });
      const presentation = getServicesPricingOfferingIconPresentation(iconKey);
      return {
        id: offering.id,
        title: offering.serviceTitle,
        description: offering.description,
        tagLabel: offering.serviceTagLabels?.[0],
        deliverables: offering.deliverables,
        industries: offering.targetIndustryLabels?.length
          ? offering.targetIndustryLabels
          : [],
        priceLabel: formatOfferingPriceLabel({
          pricingModel: offering.pricingModel,
          priceMinCents: offering.priceMinCents,
          priceMaxCents: offering.priceMaxCents,
          currency: offering.currency,
          fromLabel: labels?.fromLabel,
          quoteRequiredLabel: labels?.quoteRequiredLabel,
        }),
        priceMinCents: offering.priceMinCents,
        turnaroundLabel: offering.turnaroundLabel ?? formatOfferingTurnaroundLabel(
          offering.turnaroundDaysMin,
          offering.turnaroundDaysMax,
        ),
        iconKey,
        visualTone: presentation.tone,
        ...(offering.purchaseCount !== undefined ? { purchaseCount: offering.purchaseCount } : {}),
        ...(offering.averageRating !== undefined ? { averageRating: offering.averageRating } : {}),
        ...(offering.reviewCount !== undefined ? { reviewCount: offering.reviewCount } : {}),
        ...(offering.featuredReviewerInitials?.length ? { featuredReviewerInitials: offering.featuredReviewerInitials } : {}),
      };
    });
}

export function buildServicesPricingConditionDisplays(
  conditions: readonly ServiceConditionInput[],
): readonly ServicesPricingConditionDisplay[] {
  return conditions
    .filter((condition) => condition.conditionStatus === "active")
    .map((condition) => ({
      id: condition.id,
      title: condition.conditionTitle,
      description: condition.conditionDescription,
    }));
}

export function buildServicesPricingSkillDisplays(
  skills: readonly SkillInput[],
): readonly ServicesPricingSkillDisplay[] {
  const activeSkills = skills.filter((skill) => skill.validationStatus !== "archived" && skill.validationStatus !== "rejected");
  const maxValidations = Math.max(1, ...activeSkills.map((skill) => skill.confirmationCount ?? 0));
  return activeSkills
    .map((skill) => {
      const iconKey = resolveServicesPricingSkillIconKey(skill.skillKey, skill.iconKey);
      return {
        id: skill.id,
        name: skill.skillKey.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase()),
        validationCount: skill.confirmationCount ?? 0,
        validationPercent: Math.round(((skill.confirmationCount ?? 0) / maxValidations) * 100),
        validatorInitials: skill.featuredValidatorInitials ?? [],
        iconKey,
        visualTone: skillVisualToneFromIconKey(iconKey),
      };
    })
    .sort((left, right) => right.validationCount - left.validationCount);
}

export function buildServicesPricingTeamSummary(
  teamProfile: TeamProfileInput,
  members: readonly TeamMemberInput[],
): ServicesPricingTeamSummaryDisplay {
  const visibleMembers = members.filter((member) =>
    member.memberStatus === "visible"
    && (member.visibilityConsentStatus === "consented" || member.visibilityConsentStatus === "admin_policy"));
  const memberLanguages = Array.from(new Set([
    ...(teamProfile?.languages ?? []),
    ...visibleMembers.flatMap((member) => member.publicLanguages ?? []),
  ]));
  const teamIndustries = Array.from(new Set(
    visibleMembers.flatMap((member) => member.publicIndustryLabels ?? []),
  ));
  const yearsExperience = visibleMembers.reduce((total, member) => total + (member.yearsExperience ?? 0), 0);
  const missionsCompleted = visibleMembers.reduce((total, member) => total + (member.missionsCompleted ?? 0), 0);
  const reviewCount = visibleMembers.reduce((total, member) => total + (member.clientReviewCount ?? 0), 0);
  const satisfaction = reviewCount === 0
    ? 0
    : Math.round(visibleMembers.reduce(
      (total, member) => total + (member.clientSatisfactionPercent ?? 0) * (member.clientReviewCount ?? 0),
      0,
    ) / reviewCount);
  const avatarMembers = visibleMembers.slice(0, 6).map((member) => ({
    id: member.id,
    displayName: member.memberDisplayName,
    initials: initialsForName(member.memberDisplayName),
    roleTitle: member.roleTitle,
  }));

  return {
    memberCount: visibleMembers.length || Number.parseInt(teamProfile?.capacityLabel ?? "0", 10) || visibleMembers.length,
    languages: memberLanguages,
    languageCount: memberLanguages.length,
    yearsExperienceLabel: yearsExperience > 0 ? `${yearsExperience}+` : "—",
    industriesServedLabel: teamIndustries.join(", "),
    industriesCount: teamIndustries.length,
    missionsCompletedLabel: missionsCompleted > 0 ? `${missionsCompleted.toLocaleString()}+` : "—",
    clientSatisfactionLabel: satisfaction > 0 ? `${satisfaction}%` : "—",
    clientSatisfactionDetail: reviewCount > 0 ? `Based on ${reviewCount} client reviews` : undefined,
    members: avatarMembers,
    extraMemberCount: Math.max(0, visibleMembers.length - avatarMembers.length),
  };
}

export function sortServicesPricingOfferingsByPrice(
  offerings: readonly ServicesPricingOfferingDisplay[],
): readonly ServicesPricingOfferingDisplay[] {
  return [...offerings].sort((left, right) => (right.priceMinCents ?? 0) - (left.priceMinCents ?? 0));
}

export function collectOfferingIndustryFilters(
  offerings: readonly ServicesPricingOfferingDisplay[],
  allLabel: string,
): readonly string[] {
  const industries = new Set<string>(DEFAULT_OFFERING_INDUSTRIES);
  for (const offering of offerings) {
    for (const industry of offering.industries) industries.add(industry);
  }
  return [allLabel, ...Array.from(industries).filter((entry) => entry !== allLabel)];
}

export function filterOfferingsByIndustry(
  offerings: readonly ServicesPricingOfferingDisplay[],
  filter: string,
  allLabel: string,
): readonly ServicesPricingOfferingDisplay[] {
  if (filter === allLabel) return offerings;
  return offerings.filter((offering) => offering.industries.includes(filter));
}
